import { useCallback, useEffect, useState } from "react";
import { AlertCircle, MinusCircle } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { fetchChaoFinancialDeduction, saveChaoFinancialDeduction } from "@/services/chaoDeObra";
import { formatCurrency } from "@/utils/format";

interface Props {
  open: boolean;
  onClose: () => void;
  costCenterId: string;
  costCenterLabel: string;
  gerencialTotal: number;
  onSaved: (value: number) => void;
}

function parseBrlInput(raw: string): number | null {
  const cleaned = raw.replace(/\s/g, "").replace(/R\$/gi, "").replace(/\./g, "").replace(",", ".");
  const n = Number(cleaned);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export function ChaoDeducaoFinanceiraModal({
  open,
  onClose,
  costCenterId,
  costCenterLabel,
  gerencialTotal,
  onSaved,
}: Props) {
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!costCenterId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchChaoFinancialDeduction(costCenterId);
      if (res.success && res.value && res.value > 0) {
        setDraft(
          res.value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        );
      } else {
        setDraft("");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível carregar a dedução.");
      setDraft("");
    } finally {
      setLoading(false);
    }
  }, [costCenterId]);

  useEffect(() => {
    if (!open) return;
    void load();
  }, [open, load]);

  const parsed = parseBrlInput(draft);
  const valorAjustado =
    parsed != null ? Math.max(0, gerencialTotal - parsed) : gerencialTotal;

  const onSave = async () => {
    const value = parseBrlInput(draft);
    if (value == null) {
      setError("Informe um valor válido (use 0 para remover a dedução).");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await saveChaoFinancialDeduction(costCenterId, value);
      if (!res.success) {
        setError(res.error ?? "Falha ao salvar.");
        return;
      }
      onSaved(value);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Dedução do valor realizado" size="md">
      <div className="flex flex-col gap-4">
        <p className="text-sm text-slate-600">
          Obra: <strong>{costCenterLabel}</strong>
          <br />
          <span className="text-slate-500">
            Este valor será <strong>deduzido do valor realizado</strong> apenas neste painel (Chão de
            Obra). O avanço financeiro (%) será recalculado automaticamente. Use{" "}
            <strong>0</strong> para remover a dedução.
          </span>
        </p>

        <p className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 tabular-nums">
          Valor realizado atual (Gerencial): <strong>{formatCurrency(gerencialTotal)}</strong>
        </p>

        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Valor a deduzir (R$)
          </span>
          <div className="mt-1 relative">
            <MinusCircle className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              inputMode="decimal"
              className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/30"
              placeholder="0,00"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              disabled={loading || saving}
            />
          </div>
        </label>

        {parsed != null && (
          <p className="text-xs text-slate-600 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 tabular-nums">
            Valor realizado após dedução: <strong>{formatCurrency(valorAjustado)}</strong>
          </p>
        )}

        {error && (
          <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={() => void onSave()} loading={saving} disabled={loading}>
            Salvar
          </Button>
        </div>
      </div>
    </Modal>
  );
}
